import { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Instagram, Music2, Calendar, Mail, Plus, Edit2, Trophy, Sparkles, X, Check } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRIC_ICONS = {
  instagram_followers: Instagram,
  tiktok_followers: Music2,
  monthly_gigs: Calendar,
  email_list: Mail
};

const METRIC_COLORS = {
  instagram_followers: '#E1306C',
  tiktok_followers: '#00f2ea',
  monthly_gigs: '#D4AF37',
  email_list: '#22c55e'
};

const METRIC_LABELS = {
  instagram_followers: 'Instagram Followers',
  tiktok_followers: 'TikTok Followers',
  monthly_gigs: 'Monthly Gigs',
  email_list: 'Email List'
};

export const GrowthTracker = ({ user }) => {
  const [metrics, setMetrics] = useState([]);
  const [badges, setBadges] = useState([]);
  const [allBadges, setAllBadges] = useState({});
  const [allowedMetrics, setAllowedMetrics] = useState([]);
  const [tier, setTier] = useState('starter');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [newValue, setNewValue] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newBadge, setNewBadge] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/growth/metrics`);
      setMetrics(response.data.metrics || []);
      setBadges(response.data.badges || []);
      setAllBadges(response.data.all_badges || {});
      setAllowedMetrics(response.data.allowed_metrics || []);
      setTier(response.data.tier);
    } catch (error) {
      toast.error('Failed to load growth data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddMetric = async () => {
    if (!selectedMetric || !newValue) return;
    
    try {
      const response = await axios.post(`${API}/growth/metrics`, {
        metric_type: selectedMetric,
        value: parseInt(newValue),
        notes: newNotes || null
      });
      
      // Check for new badges
      if (response.data.new_badges?.length > 0) {
        setNewBadge(response.data.new_badges[0]);
        setTimeout(() => setNewBadge(null), 5000);
      }
      
      fetchData();
      setShowAddModal(false);
      setSelectedMetric(null);
      setNewValue('');
      setNewNotes('');
      toast.success('Metric added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add metric');
    }
  };

  const handleUpdateMetric = async (entryId) => {
    try {
      const response = await axios.put(`${API}/growth/metrics/${entryId}`, {
        entry_id: entryId,
        value: parseInt(newValue),
        notes: newNotes || null
      });
      
      if (response.data.new_badges?.length > 0) {
        setNewBadge(response.data.new_badges[0]);
        setTimeout(() => setNewBadge(null), 5000);
      }
      
      fetchData();
      setEditingEntry(null);
      setNewValue('');
      setNewNotes('');
      toast.success('Metric updated!');
    } catch (error) {
      toast.error('Failed to update metric');
    }
  };

  // Group metrics by type
  const getMetricHistory = (metricType) => {
    return metrics
      .filter(m => m.metric_type === metricType)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map(m => ({
        ...m,
        date: new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }));
  };

  // Calculate growth percentage
  const getGrowthPercentage = (metricType) => {
    const history = getMetricHistory(metricType);
    if (history.length < 2) return null;
    
    const oldest = history[0].value;
    const newest = history[history.length - 1].value;
    
    if (oldest === 0) return newest > 0 ? 100 : 0;
    return ((newest - oldest) / oldest * 100).toFixed(1);
  };

  // Get latest value for a metric
  const getLatestValue = (metricType) => {
    const history = getMetricHistory(metricType);
    return history.length > 0 ? history[history.length - 1].value : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="growth-tracker">
      {/* New Badge Celebration */}
      {newBadge && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 animate-in fade-in"
          onClick={() => setNewBadge(null)}
        >
          <div className="text-center p-8 rounded-2xl animate-in zoom-in" style={{ background: '#1a1a1a', border: '2px solid #D4AF37' }}>
            <div className="text-6xl mb-4 animate-bounce">{newBadge.icon}</div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#D4AF37' }}>Badge Unlocked!</h2>
            <p className="text-xl font-semibold mb-2">{newBadge.name}</p>
            <p className="text-gray-400">{newBadge.description}</p>
            <Sparkles className="mx-auto mt-4 text-[#D4AF37] animate-pulse" size={32} />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <TrendingUp size={24} style={{ color: '#D4AF37' }} />
            Growth Tracker
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Track your DJ career progress
            {tier !== 'elite' && tier !== 'pro' && (
              <span className="ml-2 text-yellow-500">(Upgrade to track more metrics!)</span>
            )}
          </p>
        </div>
        
        <Button
          onClick={() => setShowAddModal(true)}
          className="rounded-full"
          style={{ background: '#D4AF37', color: '#000' }}
          data-testid="add-metric-btn"
        >
          <Plus size={16} className="mr-1" />
          Log Progress
        </Button>
      </div>

      {/* Badges Section */}
      {badges.length > 0 && (
        <div className="p-4 rounded-xl" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Trophy size={18} style={{ color: '#D4AF37' }} />
            Your Badges ({badges.length})
          </h3>
          <div className="flex flex-wrap gap-3">
            {badges.map((badge, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/10"
                title={badge.description}
              >
                <span className="text-lg">{badge.icon}</span>
                <span className="text-sm font-medium">{badge.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {allowedMetrics.map((metricType) => {
          const Icon = METRIC_ICONS[metricType];
          const color = METRIC_COLORS[metricType];
          const history = getMetricHistory(metricType);
          const growth = getGrowthPercentage(metricType);
          const latest = getLatestValue(metricType);
          
          return (
            <div 
              key={metricType}
              className="p-4 rounded-xl"
              style={{ background: '#1a1a1a', border: `1px solid ${color}30` }}
            >
              {/* Metric Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
                    <Icon size={20} style={{ color }} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">{METRIC_LABELS[metricType]}</h4>
                    <p className="text-2xl font-bold" style={{ color }}>{latest.toLocaleString()}</p>
                  </div>
                </div>
                
                {growth !== null && (
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${parseFloat(growth) >= 0 ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                    {parseFloat(growth) >= 0 ? '+' : ''}{growth}%
                  </div>
                )}
              </div>
              
              {/* Chart */}
              {history.length > 1 ? (
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={history}>
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#666' }} />
                      <YAxis hide />
                      <Tooltip 
                        contentStyle={{ background: '#1a1a1a', border: `1px solid ${color}`, borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke={color} 
                        strokeWidth={2}
                        dot={{ fill: color, strokeWidth: 0, r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-center text-gray-400 text-sm py-6">
                  Add more entries to see your growth chart
                </p>
              )}
              
              {/* Recent Entries */}
              {history.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-xs text-gray-500 mb-2">Recent entries:</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {history.slice(-3).reverse().map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">{entry.date}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{entry.value.toLocaleString()}</span>
                          <button 
                            onClick={() => { setEditingEntry(entry); setNewValue(entry.value.toString()); setNewNotes(entry.notes || ''); }}
                            className="p-1 hover:bg-white/10 rounded"
                          >
                            <Edit2 size={12} className="text-gray-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Locked Metrics (for lower tiers) */}
      {tier === 'starter' && (
        <div className="p-4 rounded-xl bg-white/5 border border-dashed border-gray-600">
          <p className="text-center text-gray-400">
            <span className="font-semibold">Upgrade to Pro</span> to track Monthly Gigs and Email List size!
          </p>
        </div>
      )}

      {/* Add Metric Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setShowAddModal(false)}>
          <div 
            className="rounded-2xl p-6 border max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">Log Your Progress</h3>
              <button onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Select Metric</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {allowedMetrics.map((metricType) => {
                    const Icon = METRIC_ICONS[metricType];
                    const color = METRIC_COLORS[metricType];
                    return (
                      <button
                        key={metricType}
                        type="button"
                        onClick={() => setSelectedMetric(metricType)}
                        className={`p-3 rounded-lg border flex items-center gap-2 transition-all ${
                          selectedMetric === metricType 
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                            : 'border-gray-600 hover:border-gray-500'
                        }`}
                      >
                        <Icon size={18} style={{ color }} />
                        <span className="text-sm">{METRIC_LABELS[metricType].split(' ')[0]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <Label>Current Value</Label>
                <Input
                  type="number"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder="Enter current number"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Notes (optional)</Label>
                <Input
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder="e.g., After viral reel"
                  className="mt-1"
                />
              </div>
              
              <Button
                onClick={handleAddMetric}
                disabled={!selectedMetric || !newValue}
                className="w-full rounded-full"
                style={{ background: '#D4AF37', color: '#000' }}
              >
                Save Progress
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Entry Modal */}
      {editingEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setEditingEntry(null)}>
          <div 
            className="rounded-2xl p-6 border max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">Edit Entry</h3>
              <button onClick={() => setEditingEntry(null)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Value</Label>
                <Input
                  type="number"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Notes</Label>
                <Input
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => handleUpdateMetric(editingEntry.id)}
                  className="flex-1 rounded-full"
                  style={{ background: '#D4AF37', color: '#000' }}
                >
                  <Check size={16} className="mr-1" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditingEntry(null)}
                  className="rounded-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Elite Insights */}
      {tier === 'elite' && metrics.length > 0 && (
        <div className="p-4 rounded-xl" style={{ background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
          <h3 className="font-semibold mb-2 flex items-center gap-2 text-purple-400">
            <Sparkles size={18} />
            Elite Insights
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {allowedMetrics.map((metricType) => {
              const growth = getGrowthPercentage(metricType);
              return (
                <div key={metricType}>
                  <p className="text-xs text-gray-400">{METRIC_LABELS[metricType]}</p>
                  <p className={`text-lg font-bold ${growth !== null && parseFloat(growth) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {growth !== null ? `${parseFloat(growth) >= 0 ? '+' : ''}${growth}%` : 'N/A'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
